import { useState, useCallback, useEffect, useRef } from 'react'
import { Search, X, Calendar, Filter } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import { ProjectSelector } from '@/components/projects/ProjectSelector'
import { cn } from '@/lib/utils'
import type { RunStatus } from '@/lib/types'

/**
 * All available run status values for filtering
 */
export const RUN_STATUSES: RunStatus[] = [
  'PENDING',
  'QUEUED',
  'RUNNING',
  'SUCCEEDED',
  'FAILED',
  'TIMEOUT',
  'CANCELLED'
]

/**
 * Display labels for run statuses
 */
const STATUS_LABELS: Record<RunStatus, string> = {
  PENDING: 'Pending',
  QUEUED: 'Queued',
  RUNNING: 'Running',
  SUCCEEDED: 'Succeeded',
  FAILED: 'Failed',
  TIMEOUT: 'Timeout',
  CANCELLED: 'Cancelled'
}

/**
 * Filter parameters for runs
 */
export interface RunsFilterParams {
  search?: string
  status?: RunStatus[]
  dateFrom?: Date
  dateTo?: Date
  projectId?: string
  limit?: number
  offset?: number
}

/**
 * Props for RunFilters component
 */
export interface RunFiltersProps {
  /** Current filter values */
  filters: RunsFilterParams
  /** Callback when filters change */
  onFiltersChange: (filters: RunsFilterParams) => void
  /** Additional CSS classes */
  className?: string
}

/**
 * Run filters component - provides search, status, project, date range filters
 * All filters in a compact single-line layout matching SessionFilters
 */
export function RunFilters({
  filters,
  onFiltersChange,
  className
}: RunFiltersProps) {
  const [searchInput, setSearchInput] = useState(filters.search ?? '')
  const searchDebounceRef = useRef<NodeJS.Timeout>()

  // Debounce search input
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current)
    }
    
    searchDebounceRef.current = setTimeout(() => {
      if (searchInput !== (filters.search ?? '')) {
        onFiltersChange({
          ...filters,
          search: searchInput || undefined,
          offset: 0 // Reset pagination on search change
        })
      }
    }, 300)

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current)
      }
    }
  }, [searchInput, filters, onFiltersChange])

  // Handle status toggle
  const handleStatusToggle = useCallback((status: RunStatus) => {
    const currentStatuses = filters.status ?? []
    const newStatuses = currentStatuses.includes(status)
      ? currentStatuses.filter(s => s !== status)
      : [...currentStatuses, status]
    
    onFiltersChange({
      ...filters,
      status: newStatuses.length > 0 ? newStatuses : undefined,
      offset: 0 // Reset pagination on filter change
    })
  }, [filters, onFiltersChange])

  // Handle date from change
  const handleDateFromChange = useCallback((value: string) => {
    onFiltersChange({
      ...filters,
      dateFrom: value ? new Date(value) : undefined,
      offset: 0 // Reset pagination on filter change
    })
  }, [filters, onFiltersChange])

  // Handle date to change
  const handleDateToChange = useCallback((value: string) => {
    onFiltersChange({
      ...filters,
      dateTo: value ? new Date(value) : undefined,
      offset: 0 // Reset pagination on filter change
    })
  }, [filters, onFiltersChange])

  // Handle project change
  const handleProjectChange = useCallback((projectId: string | undefined) => {
    onFiltersChange({
      ...filters,
      projectId,
      offset: 0 // Reset pagination on filter change
    })
  }, [filters, onFiltersChange])

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    setSearchInput('')
    onFiltersChange({
      limit: filters.limit,
      offset: 0
    })
  }, [filters.limit, onFiltersChange])

  // Count active filters (excluding pagination)
  const activeFilterCount = [
    filters.search,
    filters.status && filters.status.length > 0,
    filters.dateFrom,
    filters.dateTo,
    filters.projectId
  ].filter(Boolean).length

  // Format date for input
  const formatDateForInput = (date: Date | undefined): string => {
    if (!date) return ''
    return date.toISOString().split('T')[0]
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-3', className)}>
      {/* Search Input */}
      <div className="relative flex-1 min-w-[200px] max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search by ID or goal..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="pl-9 pr-9 h-9"
          data-testid="run-search-input"
        />
        {searchInput && (
          <button
            type="button"
            onClick={() => setSearchInput('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            data-testid="run-search-clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Status Multi-Select */}
      <Popover>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            size="sm"
            className={cn(
              'gap-2 h-9',
              filters.status && filters.status.length > 0 && 'border-primary'
            )}
            data-testid="run-status-filter"
          >
            <Filter className="h-4 w-4" />
            Status
            {filters.status && filters.status.length > 0 && (
              <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs">
                {filters.status.length}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-2" align="start">
          <div className="space-y-1">
            {RUN_STATUSES.map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => handleStatusToggle(status)}
                className={cn(
                  'flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent',
                  filters.status?.includes(status) && 'bg-accent'
                )}
                data-testid={`run-status-option-${status}`}
              >
                <div className={cn(
                  'h-4 w-4 rounded border',
                  filters.status?.includes(status) 
                    ? 'bg-primary border-primary' 
                    : 'border-input'
                )}>
                  {filters.status?.includes(status) && (
                    <svg viewBox="0 0 16 16" className="h-4 w-4 text-primary-foreground">
                      <path
                        fill="currentColor"
                        d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 111.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z"
                      />
                    </svg>
                  )}
                </div>
                {STATUS_LABELS[status]}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {/* Date Range */}
      <Popover>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            size="sm"
            className={cn(
              'gap-2 h-9',
              (filters.dateFrom || filters.dateTo) && 'border-primary'
            )}
            data-testid="run-date-filter"
          >
            <Calendar className="h-4 w-4" />
            Date
            {(filters.dateFrom || filters.dateTo) && (
              <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs">
                •
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" align="start">
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">From</label>
              <Input
                type="date"
                value={formatDateForInput(filters.dateFrom)}
                onChange={(e) => handleDateFromChange(e.target.value)}
                className="w-full"
                data-testid="run-date-from"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">To</label>
              <Input
                type="date"
                value={formatDateForInput(filters.dateTo)}
                onChange={(e) => handleDateToChange(e.target.value)}
                min={formatDateForInput(filters.dateFrom)}
                className="w-full"
                data-testid="run-date-to"
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Project Selector */}
      <div className="w-[180px] shrink-0">
        <ProjectSelector
          value={filters.projectId}
          onChange={handleProjectChange}
          placeholder="All projects"
          allowClear
          className="h-9 w-full"
        />
      </div>

      {/* Clear Filters Button */}
      {activeFilterCount > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearFilters}
          className="gap-1 h-9 text-muted-foreground hover:text-foreground"
          data-testid="run-clear-filters"
        >
          <X className="h-4 w-4" />
          Clear ({activeFilterCount})
        </Button>
      )}
    </div>
  )
}
